import { map } from 'lodash';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { Button } from '@mui/material';

import { resolveMultiRxReview } from 'store/thunks/workQueueThunks';
import { EnableInventoryLookup } from 'common/utils';

import ResolveMultiRxReviewDialog from './ResolveMultiRxReviewDialog';

const ModifyItemButton = ({
  rxNumber,
  patientId,
  lineItemId,
  prescriptionId,
  instructions,
  prescriptionData,
  sx,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [savedFormData, setSavedFormData] = useState(null);
  const [viewResolveModal, setViewResolveModal] = useState(false);
  const [triggerCloseDialog, setTriggerCloseDialog] = useState(null);

  const defaultInitialValues = useMemo(() => ({ prescriptionInput: [{}] }), []);
  const initialValues = useMemo(
    () =>
      savedFormData ? { prescriptionInput: savedFormData.prescriptionInput } : defaultInitialValues,
    [savedFormData, defaultInitialValues]
  );
  const mergedPrescriptionData = useMemo(
    () => ({ ...prescriptionData, instructions }),
    [prescriptionData, instructions]
  );

  const handleViewResolveModal = () => setViewResolveModal(true);
  const closeResolveModal = () => setViewResolveModal(false);
  const handleModifyOrder = (multiOrderRecords) => {
    setSavedFormData(multiOrderRecords);
    setLoading(true);

    dispatch(
      resolveMultiRxReview({
        patientId,
        originalPrescriptionId: prescriptionId,
        originalLineItemId: lineItemId,
        multiOrderItemInput: map(multiOrderRecords.prescriptionInput, (record) => ({
          ...(EnableInventoryLookup
            ? { productId: record.drug.productId }
            : { drugProfileId: record.drug.drugProfileId }),
          sigId: record.sig.sigId,
          refills: record?.refills,
          quantity: record?.quantity,
          note: record?.notes,
          ...(record?.expirationDate && {
            expirationDate: format(new Date(record.expirationDate), 'yyyy-MM-dd'),
          }),
        })),
      })
    )
      .then(() => {
        closeResolveModal();
        setLoading(false);
        setSavedFormData(null);
        enqueueSnackbar('Prescription Successfully resolved!', { variant: 'success' });
        setTriggerCloseDialog(true);
      })
      .catch(() => {
        // POM-5805: the error dialog is already surfaced by handleError in the thunk; just
        // reset loading and keep the modal open for retry (no success snackbar on failure).
        setLoading(false);
      });
  };

  return (
    <>
      <Form
        onSubmit={handleModifyOrder}
        initialValues={initialValues}
        render={({ handleSubmit, invalid }) => (
          <ResolveMultiRxReviewDialog
            rxNumber={rxNumber}
            showModal={viewResolveModal}
            onClose={closeResolveModal}
            loading={loading}
            prescriptionId={prescriptionId}
            invalid={invalid}
            handleSubmit={handleSubmit}
            prescriptionData={mergedPrescriptionData}
            onCloseTrigger={triggerCloseDialog}
          />
        )}
      />

      <Button
        variant='contained'
        color='primary'
        onClick={handleViewResolveModal}
        size='small'
        sx={{
          px: 1,
          minHeight: 30,
          fontSize: '0.75rem',
          lineHeight: 'normal',
          minWidth: '5',
          textTransform: 'none',
          ...sx,
        }}
      >
        Modify Item
      </Button>
    </>
  );
};

export default ModifyItemButton;

ModifyItemButton.propTypes = {
  instructions: PropTypes.string,
  rxNumber: PropTypes.string.isRequired,
  patientId: PropTypes.string.isRequired,
  lineItemId: PropTypes.string.isRequired,
  prescriptionId: PropTypes.string.isRequired,
  prescriptionData: PropTypes.shape({}).isRequired,
  sx: PropTypes.shape({}),
};

ModifyItemButton.defaultProps = { instructions: null, sx: {} };
